import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useApp } from '../context/AppContext';

const GlobalSearch = ({ isOpen, onClose, onOpenVehicle, onOpenInvoice }) => {
  const { vehicles, invoices } = useApp();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const q = (query || '').trim().toLowerCase();

  const { vehicleResults, invoiceResults } = useMemo(() => {
    if (!q) {
      return { vehicleResults: [], invoiceResults: [] };
    }
    const vehicleResults = vehicles
      .map((v, idx) => ({ vehicle: v, index: idx }))
      .filter(({ vehicle }) => {
        if (vehicle.deletedAt) return false;
        const reg = (vehicle.reg || '').toLowerCase();
        const vehicleName = (vehicle.vehicle || '').toLowerCase();
        const customer = (vehicle.customerName || '').toLowerCase();
        const contact = (vehicle.contact || '').toLowerCase();
        return reg.includes(q) || vehicleName.includes(q) || customer.includes(q) || contact.includes(q);
      })
      .slice(0, 15);
    const invoiceResults = (invoices || [])
      .filter((inv) => {
        const num = (inv.number || '').toLowerCase();
        const reg = (inv.reg || '').toLowerCase();
        const vehicle = (inv.vehicle || '').toLowerCase();
        const contact = (inv.contact || '').toLowerCase();
        return num.includes(q) || reg.includes(q) || vehicle.includes(q) || contact.includes(q);
      })
      .slice(0, 10);
    return { vehicleResults, invoiceResults };
  }, [vehicles, invoices, q]);

  const handleSelectVehicle = (index) => {
    onOpenVehicle(index);
    onClose();
  };

  const handleSelectInvoice = (invoice) => {
    onOpenInvoice(invoice);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        role="presentation"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 9998,
          animation: 'fadeIn 0.15s ease-out'
        }}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-label="Search vehicles and invoices"
        style={{
          position: 'fixed',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(90vw, 520px)',
          maxHeight: '70vh',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'fadeIn 0.15s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: '#6b7280' }}>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              placeholder="Search by reg, customer, vehicle or invoice number..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                color: '#111'
              }}
            />
            <kbd style={{ fontSize: '11px', color: '#9ca3af', padding: '2px 6px', background: '#f3f4f6', borderRadius: '4px' }}>Esc</kbd>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {!q && (
            <div style={{ padding: '24px 16px', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
              Type to search vehicles and invoices. Use reg, customer name, vehicle or invoice number.
            </div>
          )}
          {q && vehicleResults.length === 0 && invoiceResults.length === 0 && (
            <div style={{ padding: '24px 16px', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
              No results for &quot;{query}&quot;
            </div>
          )}
          {q && vehicleResults.length > 0 && (
            <div style={{ padding: '0 16px 8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Vehicles</div>
              {vehicleResults.map(({ vehicle, index }) => (
                <button
                  key={`v-${index}`}
                  type="button"
                  onClick={() => handleSelectVehicle(index)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    marginBottom: '4px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#111'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <span style={{ fontWeight: 600 }}>{vehicle.reg}</span>
                  {vehicle.vehicle && <span style={{ color: '#6b7280' }}> · {vehicle.vehicle}</span>}
                  {(vehicle.customerName || vehicle.contact) && (
                    <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {[vehicle.customerName, vehicle.contact].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {q && invoiceResults.length > 0 && (
            <div style={{ padding: '0 16px 8px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Invoices</div>
              {invoiceResults.map((inv) => (
                <button
                  key={inv.number}
                  type="button"
                  onClick={() => handleSelectInvoice(inv)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    marginBottom: '4px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#111'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <span style={{ fontWeight: 600 }}>{inv.number}</span>
                  {inv.reg && <span style={{ color: '#6b7280' }}> · {inv.reg}</span>}
                  {inv.vehicle && <span style={{ color: '#6b7280' }}> · {inv.vehicle}</span>}
                  {(inv.contact || inv.total != null) && (
                    <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                      {[inv.contact, inv.total != null && `£${Number(inv.total).toFixed(2)}`].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;
